// exportbestanden.js — alles wat de browser zelf maakt voor de uitvoer:
// de tekeningen als afbeelding, de kaartbestanden en de opbouw van het
// strategisch laadplan.
//
// Waarom hier en niet op de server: een tekening omzetten naar een afbeelding
// kan de browser zelf, en de server hoeft dan geen beeldbewerking te draaien.
// De server krijgt alleen de kant-en-klare blokken en doet de opmaak.

// ── Kleuren, gelijk aan de app ─────────────────────────────────────────
export const EK = {
  accent:'#1F5C6B', accent2:'#2D7F92', oker:'#9C6420', ink:'#12161C', ink2:'#3D4652',
  ink3:'#5B6674', ink4:'#8A94A1', licht:'#EDEBE6', lijn:'#D9D5CD', wit:'#FFFFFF',
  ac:'#2D7F92', dc:'#D9A02B', hpc:'#B4442F',
  bew:'#1F5C6B', bez:'#9CC2CC', log:'#3D4652',
  ok:'#3F7D4E',
};
// Vijf tinten oker, dezelfde schaal als op de kaart in de app.
export const SCHAAL = ['#EBD8BE', '#DCBB92', '#C79A63', '#9C6420', '#7A4E19'];

const esc = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const nl = (n) => Math.round(n || 0).toLocaleString('nl-BE');

// ── Tekening omzetten naar een afbeelding ──────────────────────────────
// De browser tekent de SVG op een canvas en levert een PNG. Werkt alleen als
// er geen externe afbeeldingen in de tekening zitten; die zijn er niet, want
// we tekenen alles zelf met vormen.
//
// De achtergrondkleur is instelbaar en dat is niet vrijblijvend: de omslag
// tekent de gemeentevorm in WIT, en op een witte achtergrond is die dus
// onzichtbaar. Voor die ene tekening moet hier de petrolkleur van het vlak
// worden meegegeven.
export function svgNaarPng(svg, breedte = 1200, achtergrond = '#FFFFFF') {
  return new Promise((klaar, mislukt) => {
    const maten = svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
    const b = maten ? Number(maten[1]) : 620, h = maten ? Number(maten[2]) : 400;
    const schaal = breedte / b;
    const img = new Image();
    img.onload = () => {
      try {
        const doek = document.createElement('canvas');
        doek.width = Math.round(b * schaal);
        doek.height = Math.round(h * schaal);
        const ctx = doek.getContext('2d');
        ctx.fillStyle = achtergrond;
        ctx.fillRect(0, 0, doek.width, doek.height);
        ctx.drawImage(img, 0, 0, doek.width, doek.height);
        klaar(doek.toDataURL('image/png').split(',')[1]);
      } catch (e) { mislukt(e); }
    };
    img.onerror = () => mislukt(new Error('de tekening kon niet worden omgezet'));
    // Base64 in plaats van een blob-adres: dat laatste geeft in sommige
    // browsers een beveiligingsfout bij het uitlezen van het canvas.
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  });
}

// ── Vormen uit de sectoren halen ───────────────────────────────────────
// Geeft per wijk de ringen terug, in graden. Eén plek, zodat de stadskaart en
// de wijkkaart gegarandeerd dezelfde vormen tekenen.
// Dezelfde groepering, maar per statistische sector. Nodig om de kaart te
// kleuren met de meting die bij die sector hoort, in plaats van met een
// gemiddelde over een hele wijk.
export function ringenPerSector(sectorenGeo) {
  const uit = new Map();
  for (const f of (sectorenGeo?.features || [])) {
    const code = f.properties?.NISCODE;
    const g = f.geometry;
    if (!code || !g) continue;
    const ringen = [];
    if (g.type === 'Polygon') g.coordinates.forEach(r => ringen.push(r));
    else if (g.type === 'MultiPolygon') g.coordinates.forEach(pl => pl.forEach(r => ringen.push(r)));
    if (!uit.has(code)) uit.set(code, []);
    uit.get(code).push(...ringen.filter(r => r.length > 2));
  }
  return uit;
}

export function ringenPerWijk(sectorenGeo) {
  const uit = new Map();
  for (const f of (sectorenGeo?.features || [])) {
    const id = f.properties?.WIJK_ID;
    const g = f.geometry;
    if (!id || !g) continue;
    const ringen = [];
    if (g.type === 'Polygon') g.coordinates.forEach(r => ringen.push(r));
    else if (g.type === 'MultiPolygon') g.coordinates.forEach(p => p.forEach(r => ringen.push(r)));
    if (!uit.has(id)) uit.set(id, []);
    uit.get(id).push(...ringen.filter(r => r.length > 2));
  }
  return uit;
}

// Rekent graden om naar tekenvlak. Breedtegraden lopen op het scherm omgekeerd,
// en op onze breedte is een graad lengte korter dan een graad breedte.
function maakProjectie(ringen, B, H, marge = 24) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  ringen.forEach(r => r.forEach(([x, y]) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }));
  if (!Number.isFinite(minX) || maxX === minX || maxY === minY) return null;
  const rekX = Math.cos((minY + maxY) / 2 * Math.PI / 180);
  const brX = (maxX - minX) * rekX, brY = maxY - minY;
  const schaal = Math.min((B - marge * 2) / brX, (H - marge * 2) / brY);
  const dx = (B - brX * schaal) / 2, dy = (H - brY * schaal) / 2;
  const punt = (x, y) => [dx + (x - minX) * rekX * schaal, dy + (maxY - y) * schaal];
  // Meters per tekeneenheid, voor de schaalbalk.
  const mPerEenheid = 111320 * Math.cos((minY + maxY) / 2 * Math.PI / 180) / (rekX * schaal) * rekX;
  return { punt, schaal, mPerEenheid: 111320 / schaal * 1 };
}

function pad(ring, p) {
  const punten = ring.filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y))
    .map(([x, y]) => p.punt(x, y).map(v => v.toFixed(1)).join(','));
  return punten.length > 2 ? 'M' + punten.join('L') + 'Z' : null;
}

// Schaalbalk en noordpijl, op elke kaart hetzelfde.
function kompas(B, H, mPerEenheid) {
  // Een ronde afstand kiezen die ongeveer een zesde van de breedte beslaat.
  const doel = (B / 6) * mPerEenheid;
  const stappen = [100, 200, 250, 500, 1000, 2000, 5000, 10000];
  const m = stappen.reduce((a, b) => Math.abs(b - doel) < Math.abs(a - doel) ? b : a, stappen[0]);
  const lengte = m / mPerEenheid;
  const tekst = m >= 1000 ? `${m / 1000} km` : `${m} m`;
  return `
    <g transform="translate(${B - 42},34)">
      <path d="M0 -16 L6 7 L0 2 L-6 7 Z" fill="${EK.ink2}"/>
      <text x="0" y="22" font-family="Calibri, sans-serif" font-size="11" fill="${EK.ink2}" text-anchor="middle">N</text>
    </g>
    <g>
      <rect x="24" y="${H - 26}" width="${lengte.toFixed(1)}" height="4" fill="${EK.ink2}"/>
      <text x="24" y="${H - 32}" font-family="Calibri, sans-serif" font-size="11" fill="${EK.ink2}">0</text>
      <text x="${24 + lengte}" y="${H - 32}" font-family="Calibri, sans-serif" font-size="11"
            fill="${EK.ink2}" text-anchor="end">${tekst}</text>
    </g>`;
}

// ── Stadskaart ─────────────────────────────────────────────────────────
// Elke wijk in de tint die hoort bij wat er dat jaar bij moet komen, met de
// aangewezen locaties als stip.
export function stadskaartSvg(sectorenGeo, wijken, waardeVan, locaties = [], B = 620, H = 430, opties = {}) {
  const perWijk = ringenPerWijk(sectorenGeo);
  const alle = [];
  perWijk.forEach(r => alle.push(...r));
  if (!alle.length) return null;
  const p = maakProjectie(alle, B, H);
  if (!p) return null;

  // Twee manieren om in te kleuren. Standaard naar de waarde per wijk, van
  // licht naar donker. Of, als er een netruimte per wijk wordt meegegeven, in
  // de kleuren van de netbeheerder: krap, beperkt of ruim. Dan zegt de kaart
  // iets wat niet al in de tabel ernaast staat.
  const NET_KLEUR = { krap:'#E08A2E', beperkt:'#E8DE2A', ruim:'#8CC63F' };
  const netruimteVan = opties.netruimteVan || null;
  const labelVan     = opties.labelVan || null;
  // Is de meting per sector meegegeven, dan wordt elke sector met zijn eigen
  // kleur getekend en blijven de wijken alleen over voor het opschrift.
  const perSector    = opties.netruimtePerSector || null;
  // Opschrift per sector, naast dat per wijk. Krijgt een kleinere letter,
  // want er zijn er veel meer.
  const labelSector  = opties.labelPerSector || null;
  // Tekst die verschijnt als de muis boven een sector hangt.
  const titelSector  = opties.titelPerSector || null;

  const waarden = wijken.map(w => waardeVan(w) || 0).filter(v => v > 0).sort((a, b) => a - b);
  const grens = (i) => waarden.length ? waarden[Math.floor(waarden.length * i / 5)] : 0;
  const tint = (v) => {
    if (!v) return SCHAAL[0];
    for (let i = 4; i >= 1; i--) if (v >= grens(i)) return SCHAAL[i];
    return SCHAAL[0];
  };

  const vlakken = [];
  const opschriften = [];
  if (perSector || labelSector) {
    for (const [code, ringen] of ringenPerSector(sectorenGeo)) {
      if (perSector) {
        const kleur = NET_KLEUR[perSector[code]] || EK.licht;
        const titel = titelSector ? titelSector(code) : '';
        for (const r of ringen) {
          const d = pad(r, p);
          if (!d) continue;
          vlakken.push(titel
            ? `<path d="${d}" fill="${kleur}" stroke="#ffffff" stroke-width="0.6"><title>${titel}</title></path>`
            : `<path d="${d}" fill="${kleur}" stroke="#ffffff" stroke-width="0.6"/>`);
        }
      }
      if (labelSector && ringen.length) {
        const tekst = labelSector(code);
        if (tekst) {
          const grootste = ringen.reduce((a2, r) => (r.length > a2.length ? r : a2), ringen[0]);
          const punten = grootste.map(c => p.punt(c[0], c[1]));
          const x = punten.reduce((a2, q) => a2 + q[0], 0) / punten.length;
          const y = punten.reduce((a2, q) => a2 + q[1], 0) / punten.length;
          opschriften.push(
            `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="middle" ` +
            `font-family="Calibri, sans-serif" font-size="9.5" font-weight="600" ` +
            `fill="${EK.ink}" stroke="#ffffff" stroke-width="2.4" paint-order="stroke">${tekst}</text>`);
        }
      }
    }
  }
  for (const w of wijken) {
    const ringen = perWijk.get(w.id) || [];
    const kleur = netruimteVan
      ? (NET_KLEUR[netruimteVan(w)] || EK.licht)
      : tint(waardeVan(w) || 0);
    if (!perSector) for (const r of ringen) {
      const d = pad(r, p);
      if (d) vlakken.push(`<path d="${d}" fill="${kleur}" stroke="#ffffff" stroke-width="1.1"/>`);
    }
    // Het opschrift in het midden van de grootste ring van de wijk.
    if (labelVan && ringen.length) {
      const tekst = labelVan(w);
      if (tekst) {
        const grootste = ringen.reduce((a, r) => (r.length > a.length ? r : a), ringen[0]);
        const punten = grootste.map(c => p.punt(c[0], c[1]));
        const x = punten.reduce((a, q) => a + q[0], 0) / punten.length;
        const y = punten.reduce((a, q) => a + q[1], 0) / punten.length;
        opschriften.push(
          `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="middle" ` +
          `font-family="Calibri, sans-serif" font-size="15" font-weight="700" ` +
          `fill="${EK.ink}" stroke="#ffffff" stroke-width="3.5" paint-order="stroke">${tekst}</text>`);
      }
    }
  }
  const stippen = locaties.filter(l => Number.isFinite(l.lat) && Number.isFinite(l.lng))
    .map(l => { const [x, y] = p.punt(l.lng, l.lat);
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="7" fill="${EK.accent}" stroke="#fff" stroke-width="2"/>`; });

  const legenda = netruimteVan
    ? [['krap','krap'], ['beperkt','beperkt'], ['ruim','ruim']].map(([k, tekst], i) => `
        <rect x="${24 + i * 96}" y="${H - 58}" width="16" height="11" fill="${NET_KLEUR[k]}" stroke="#fff"/>
        <text x="${44 + i * 96}" y="${H - 49}" font-family="Calibri, sans-serif" font-size="11" fill="${EK.ink2}">${tekst}</text>`).join('')
    : SCHAAL.map((k, i) => `
        <rect x="${24 + i * 74}" y="${H - 58}" width="16" height="11" fill="${k}" stroke="#fff"/>
        <text x="${44 + i * 74}" y="${H - 49}" font-family="Calibri, sans-serif" font-size="11" fill="${EK.ink2}">${
          i === 0 ? 'minst' : i === 4 ? 'meest' : ''}</text>`).join('');
  const stipX = netruimteVan ? 24 + 3 * 96 + 10 : 24 + 5 * 74 + 10;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${B} ${H}" width="${B}" height="${H}">
    <rect width="${B}" height="${H}" fill="${EK.licht}"/>
    ${vlakken.join('')}${stippen.join('')}${opschriften.join('')}${legenda}
    <circle cx="${stipX}" cy="${H - 53}" r="6" fill="${EK.accent}" stroke="#fff" stroke-width="2"/>
    <text x="${stipX + 12}" y="${H - 49}" font-family="Calibri, sans-serif" font-size="11" fill="${EK.ink2}">laadplein</text>
    ${kompas(B, H, p.mPerEenheid)}
  </svg>`;
}

// ── Kaart van één wijk ─────────────────────────────────────────────────
// De wijk in kleur, de rest van de gemeente in grijs, en de aangewezen
// locaties genummerd zodat ze aansluiten op de tabel eronder.
// De dekkingskaart voor het rapport. Gebruikt DEZELFDE afbeelding als op het
// scherm: de laag met de vakjes zonder laadpunt binnen 250 meter komt als een
// PNG binnen en wordt hier op de juiste plek gelegd. Zo kan papier niet iets
// anders zeggen dan het scherm.
//
// opties: { beeldPng, beeldGrenzen, punten, pleinen, straalM,
//           plaatsing, kleuren, jaar }
//   kleuren zijn die van tabblad Dekking ({ publiek, snel, semi, plein, plan,
//   rood }), zodat papier dezelfde kleuren toont als het scherm.
//   plaatsing zijn de laadpunten uit het plan tot en met het peiljaar, zodat
//   de kaart dezelfde punten toont als het rood en de getallen meetellen.
//   beeldGrenzen is [[zuid, west], [noord, oost]] zoals de kaart het teruggeeft.
export function dekkingskaartSvg(sectorenGeo, opties = {}, B = 620, H = 560) {
  const perSector = ringenPerSector(sectorenGeo);
  const alleRingen = [...perSector.values()].flat();
  if (!alleRingen.length) return null;
  const p = maakProjectie(alleRingen, B, H);
  if (!p) return null;

  const delen = [];

  // De rode laag onderop, uitgerekt tussen de twee hoeken die meekomen.
  if (opties.beeldPng && opties.beeldGrenzen) {
    const [[zuid, west], [noord, oost]] = opties.beeldGrenzen;
    const [x0, y0] = p.punt(west, noord);
    const [x1, y1] = p.punt(oost, zuid);
    delen.push(`<image href="${opties.beeldPng}" x="${x0.toFixed(1)}" y="${y0.toFixed(1)}" ` +
      `width="${(x1 - x0).toFixed(1)}" height="${(y1 - y0).toFixed(1)}" ` +
      `preserveAspectRatio="none" image-rendering="pixelated"/>`);
  }

  // De sectorgrenzen erbovenop, zonder vulling zodat het rood zichtbaar blijft.
  for (const ringen of perSector.values()) {
    for (const r of ringen) {
      const d = pad(r, p);
      if (d) delen.push(`<path d="${d}" fill="none" stroke="#8A94A0" stroke-width="0.6"/>`);
    }
  }

  const KL = { publiek:'#2D7F92', snel:'#D9A02B', semi:'#9AA4B1', plein:'#D6338A',
               plan:'#6B4C9A', rood:'#A32A16', ...(opties.kleuren || {}) };
  const soortVan = (l) => (l.semi ? 'semi' : l.snel ? 'snel' : 'publiek');

  // De cirkels van 250 meter, en daarna de laadpunten zelf.
  const straal = (opties.straalM || 250) / p.mPerEenheid;
  const stip = (l, kleur, r) => {
    const [x, y] = p.punt(l.lng, l.lat);
    return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r}" fill="${kleur}" ` +
           `stroke="#ffffff" stroke-width="0.8"/>`;
  };
  for (const l of (opties.punten || [])) {
    const [x, y] = p.punt(l.lng, l.lat);
    const k = KL[soortVan(l)];
    delen.push(`<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${straal.toFixed(1)}" ` +
      `fill="${k}" fill-opacity="0.10" stroke="${k}" stroke-opacity="0.35" stroke-width="0.5"/>`);
  }
  for (const l of (opties.pleinen || [])) {
    const [x, y] = p.punt(l.lng, l.lat);
    delen.push(`<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${straal.toFixed(1)}" ` +
      `fill="${KL.plein}" fill-opacity="0.12" stroke="${KL.plein}" stroke-opacity="0.4" stroke-width="0.5"/>`);
  }
  const planKleur = KL.plan;
  for (const l of (opties.plaatsing || [])) {
    const [x, y] = p.punt(l.lng, l.lat);
    delen.push(`<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${straal.toFixed(1)}" ` +
      `fill="${planKleur}" fill-opacity="0.12" stroke="${planKleur}" stroke-opacity="0.4" stroke-width="0.5"/>`);
  }
  for (const l of (opties.punten || [])) delen.push(stip(l, KL[soortVan(l)], 1.8));
  for (const l of (opties.pleinen || [])) delen.push(stip(l, KL.plein, 3.2));
  for (const l of (opties.plaatsing || [])) delen.push(stip(l, planKleur, 2.6));

  // Legenda linksonder, boven de schaalbalk. Stond hij op dezelfde hoogte, dan
  // liep de schaalbalk door de eerste regel heen. Alleen de soorten die op de
  // kaart staan; past het niet op één regel, dan gaat de rest naar een regel
  // erboven. De breedte per tekst is geschat op 5,2 punten per letter.
  const soorten = new Set((opties.punten || []).map(soortVan));
  const regels = [
    ['vierkant', KL.rood, `geen laadpunt binnen ${opties.straalM || 250} m`],
    ...(soorten.has('publiek') ? [['rond', KL.publiek, 'publiek gewone paal']] : []),
    ...(soorten.has('snel')    ? [['rond', KL.snel,    'publiek snellader']]   : []),
    ...(soorten.has('semi')    ? [['rond', KL.semi,    'semipubliek']]         : []),
    ...((opties.pleinen || []).length ? [['rond', KL.plein, 'aangewezen laadplein']] : []),
    ...((opties.plaatsing || []).length
      ? [['rond', planKleur, `nieuw${opties.jaar ? ` tot en met ${opties.jaar}` : ''}`]] : []),
  ];
  const legenda = [];
  let lx = 16, ly = H - 44;
  const rijen = [[]];
  for (const r of regels) {
    const breed = 22 + r[2].length * 5.2;
    if (lx + breed > B - 16 && rijen[rijen.length - 1].length) { rijen.push([]); lx = 16; }
    rijen[rijen.length - 1].push([...r, lx]);
    lx += breed + 14;
  }
  rijen.forEach((rij, n) => {
    const y = ly - (rijen.length - 1 - n) * 16;
    for (const [vorm, kleur, tekst, x] of rij) {
      legenda.push(vorm === 'vierkant'
        ? `<rect x="${x}" y="${y - 14}" width="11" height="11" fill="${kleur}" fill-opacity="0.85"/>`
        : `<circle cx="${x + 5}" cy="${y - 9}" r="4" fill="${kleur}"/>`);
      legenda.push(`<text x="${x + 17}" y="${y - 5}" font-family="Calibri, sans-serif" font-size="10" ` +
        `fill="${EK.ink}">${esc(tekst)}</text>`);
    }
  });

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${B} ${H}" width="${B}" height="${H}">` +
    `<rect width="${B}" height="${H}" fill="${EK.wit}"/>` +
    delen.join('') + kompas(B, H, p.mPerEenheid) + legenda.join('') + `</svg>`;
}

export function wijkkaartSvg(sectorenGeo, wijkId, wijken, locaties = [], B = 620, H = 320) {
  const perWijk = ringenPerWijk(sectorenGeo);
  const eigen = perWijk.get(wijkId) || [];
  if (!eigen.length) return null;
  const p = maakProjectie(eigen, B, H, 30);
  if (!p) return null;

  // Buurwijken meetekenen voor de context, maar alleen wat binnen beeld valt.
  const achtergrond = [];
  perWijk.forEach((ringen, id) => {
    if (id === wijkId) return;
    for (const r of ringen) {
      const d = pad(r, p);
      if (d) achtergrond.push(`<path d="${d}" fill="#E4E1DA" stroke="#ffffff" stroke-width="1"/>`);
    }
  });
  const vlak = eigen.map(r => { const d = pad(r, p);
    return d ? `<path d="${d}" fill="#D9BE9A" stroke="#ffffff" stroke-width="1.4"/>` : ''; }).join('');

  const stippen = locaties.map((l, i) => {
    if (!Number.isFinite(l.lat) || !Number.isFinite(l.lng)) return '';
    const [x, y] = p.punt(l.lng, l.lat);
    return `<g><circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="14" fill="${EK.accent}" stroke="#fff" stroke-width="2.5"/>
      <text x="${x.toFixed(1)}" y="${(y + 5).toFixed(1)}" font-family="Calibri, sans-serif" font-size="14"
            font-weight="bold" fill="#fff" text-anchor="middle">${i + 1}</text></g>`;
  }).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${B} ${H}" width="${B}" height="${H}">
    <rect width="${B}" height="${H}" fill="${EK.licht}"/>
    ${achtergrond.join('')}${vlak}${stippen}
    ${kompas(B, H, p.mPerEenheid)}
  </svg>`;
}

// ── Staafgrafiek met lijn ──────────────────────────────────────────────
export function grafiekSvg(jaren, staven, lijn, B = 620, H = 300) {
  const L = 66, R = 18, T = 20, Bo = 46;
  const max = Math.max(1, ...lijn, ...staven) * 1.08;
  const x = (i) => L + (i + 0.5) * (B - L - R) / Math.max(1, jaren.length);
  const y = (v) => H - Bo - (v / max) * (H - T - Bo);
  const bw = (B - L - R) / Math.max(1, jaren.length) * 0.42;
  const raster = [0, 0.25, 0.5, 0.75, 1].map(f => `
    <line x1="${L}" y1="${y(max * f)}" x2="${B - R}" y2="${y(max * f)}" stroke="#E8E4DC" stroke-width="1"/>
    <text x="${L - 8}" y="${y(max * f) + 4}" font-family="Calibri, sans-serif" font-size="11"
          fill="${EK.ink4}" text-anchor="end">${nl(max * f)}</text>`).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${B} ${H}" width="${B}" height="${H}">
    <rect width="${B}" height="${H}" fill="#ffffff"/>${raster}
    ${staven.map((v, i) => `<rect x="${(x(i) - bw / 2).toFixed(1)}" y="${y(v).toFixed(1)}" width="${bw.toFixed(1)}"
        height="${(H - Bo - y(v)).toFixed(1)}" fill="${EK.accent2}" rx="3"/>`).join('')}
    <polyline points="${lijn.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')}"
              fill="none" stroke="${EK.oker}" stroke-width="2.6"/>
    ${lijn.map((v, i) => `<circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="3.4" fill="${EK.oker}"/>`).join('')}
    ${jaren.map((j, i) => `<text x="${x(i).toFixed(1)}" y="${H - Bo + 20}" font-family="Calibri, sans-serif"
        font-size="12" font-weight="bold" fill="${EK.ink2}" text-anchor="middle">${j}</text>`).join('')}
    <rect x="${L}" y="${H - 18}" width="11" height="11" fill="${EK.accent2}"/>
    <text x="${L + 17}" y="${H - 8}" font-family="Calibri, sans-serif" font-size="11.5" fill="${EK.ink2}">Bij te plaatsen dat jaar</text>
    <rect x="${L + 190}" y="${H - 18}" width="11" height="11" fill="${EK.oker}"/>
    <text x="${L + 207}" y="${H - 8}" font-family="Calibri, sans-serif" font-size="11.5" fill="${EK.ink2}">Tot en met dat jaar</text>
  </svg>`;
}

// ── Liggende verdeelbalk ───────────────────────────────────────────────
export function balkSvg(delen, kleuren, labels, B = 620, H = 78) {
  const som = delen.reduce((a, b) => a + (b || 0), 0) || 1;
  let x = 0;
  const vak = delen.map((d, i) => {
    const b = (d || 0) / som * B;
    const r = `<rect x="${x.toFixed(1)}" y="0" width="${b.toFixed(1)}" height="30" fill="${kleuren[i]}"/>`;
    x += b; return r;
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${B} ${H}" width="${B}" height="${H}">
    <rect width="${B}" height="${H}" fill="#ffffff"/>${vak.join('')}
    ${labels.map((l, i) => `<rect x="${i * 205}" y="48" width="11" height="11" fill="${kleuren[i]}"/>
      <text x="${i * 205 + 17}" y="58" font-family="Calibri, sans-serif" font-size="12" fill="${EK.ink2}">${esc(l)}</text>`).join('')}
  </svg>`;
}

// ── De gemeentevorm voor de omslag ─────────────────────────────────────
export function omslagSvg(sectorenGeo, B = 620, H = 620) {
  const perWijk = ringenPerWijk(sectorenGeo);
  const alle = [];
  perWijk.forEach(r => alle.push(...r));
  if (!alle.length) return null;
  const p = maakProjectie(alle, B, H, 40);
  if (!p) return null;
  const paden = [];
  perWijk.forEach(ringen => ringen.forEach(r => {
    const d = pad(r, p);
    if (d) paden.push(`<path d="${d}" fill="none" stroke="#ffffff" stroke-width="2.6" ` +
                      `stroke-linejoin="round" opacity="0.6"/>`);
  }));
  if (!paden.length) return null;
  // De achtergrond zit IN de tekening, niet in de omzetting. Reden: de vorm
  // wordt in wit getekend, en zonder eigen achtergrond hangt het ervan af waar
  // de afbeelding belandt of je iets ziet. Op een wit vlak was het resultaat
  // een leeg wit blok. Deze kleur is dezelfde als het vlak op de titelpagina,
  // dus de afbeelding gaat er naadloos in op.
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${B} ${H}" width="${B}" height="${H}">
    <rect width="${B}" height="${H}" fill="${EK.accent}"/>${paden.join('')}</svg>`;
}

// ── Kaartbestanden ─────────────────────────────────────────────────────
export function geojsonVan(locaties, gemeente, jaar) {
  return JSON.stringify({
    type: 'FeatureCollection',
    name: `Voorziene laadlocaties ${gemeente || ''}`.trim(),
    // GeoJSON schrijft WGS84 voor; QGIS zet dat zelf om als de gemeente met
    // een ander stelsel werkt.
    crs: { type: 'name', properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' } },
    features: locaties.map((l, i) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [Number(l.lng), Number(l.lat)] },
      properties: {
        nummer: i + 1,
        naam: l.naam || 'Parkeerterrein',
        gemeente: gemeente || '',
        peiljaar: jaar || null,
        laadpalen: l.palen ?? null,
        laadpunten: l.punten ?? null,
        parkeervakken: l.vakken ?? null,
        toegang: l.groep || '',
        voorziening: l.voorziening || '',
        afstand_voorziening_m: l.voorzieningM ?? null,
        deelwagens: l.deelwagens ? 'ja' : 'onbekend',
        beoordeling: l.oordeel || '',
        notitie: l.notitie || '',
      },
    })),
  }, null, 2);
}

export function kmlVan(locaties, gemeente, jaar) {
  const plek = (l, i) => `
    <Placemark>
      <name>${esc(`${i + 1}. ${l.naam || 'Parkeerterrein'}`)}</name>
      <description>${esc(
        `${l.punten ?? '?'} laadpunten op ${l.palen ?? '?'} laadpalen · ` +
        `${l.vakken ?? '?'} parkeervakken` +
        (l.voorziening ? ` · ${l.voorziening}` : '') +
        (l.notitie ? ` · ${l.notitie}` : ''))}</description>
      <Point><coordinates>${Number(l.lng)},${Number(l.lat)},0</coordinates></Point>
    </Placemark>`;
  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2"><Document>
  <name>${esc(`Voorziene laadlocaties ${gemeente || ''} ${jaar || ''}`.trim())}</name>
  ${locaties.map(plek).join('')}
</Document></kml>`;
}

// ── Een zipbestand maken ───────────────────────────────────────────────
// Zonder hulpmiddel, want een zip zonder compressie is een eenvoudig formaat
// en de kaarten zijn al PNG, dus comprimeren levert toch niets op.
function crc32(bytes) {
  let c, tabel = crc32.tabel;
  if (!tabel) {
    tabel = crc32.tabel = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
      tabel[n] = c >>> 0;
    }
  }
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) crc = tabel[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

export function maakZip(bestanden) {
  const enc = new TextEncoder();
  const delen = [], centraal = [];
  let offset = 0;
  const getal = (n, lengte) => {
    const b = new Uint8Array(lengte);
    for (let i = 0; i < lengte; i++) b[i] = (n >>> (8 * i)) & 0xFF;
    return b;
  };
  for (const { naam, inhoud } of bestanden) {
    const naamB = enc.encode(naam);
    const data = inhoud instanceof Uint8Array ? inhoud : enc.encode(inhoud);
    const crc = crc32(data);
    const kop = [
      getal(0x04034b50, 4), getal(20, 2), getal(0, 2), getal(0, 2),
      getal(0, 2), getal(0, 2), getal(crc, 4), getal(data.length, 4),
      getal(data.length, 4), getal(naamB.length, 2), getal(0, 2), naamB,
    ];
    delen.push(...kop, data);
    centraal.push([
      getal(0x02014b50, 4), getal(20, 2), getal(20, 2), getal(0, 2), getal(0, 2),
      getal(0, 2), getal(0, 2), getal(crc, 4), getal(data.length, 4), getal(data.length, 4),
      getal(naamB.length, 2), getal(0, 2), getal(0, 2), getal(0, 2), getal(0, 2),
      getal(0, 4), getal(offset, 4), naamB,
    ]);
    offset += kop.reduce((s, d) => s + d.length, 0) + data.length;
  }
  const centraalPlat = centraal.flat();
  const centraalLengte = centraalPlat.reduce((s, d) => s + d.length, 0);
  const staart = [
    getal(0x06054b50, 4), getal(0, 2), getal(0, 2),
    getal(bestanden.length, 2), getal(bestanden.length, 2),
    getal(centraalLengte, 4), getal(offset, 4), getal(0, 2),
  ];
  const alles = [...delen, ...centraalPlat, ...staart];
  const totaal = alles.reduce((s, d) => s + d.length, 0);
  const uit = new Uint8Array(totaal);
  let pos = 0;
  for (const d of alles) { uit.set(d, pos); pos += d.length; }
  return uit;
}

// ── Bestand aanbieden om te bewaren ────────────────────────────────────
export function bewaarAls(naam, inhoud, soort) {
  const blob = inhoud instanceof Uint8Array
    ? new Blob([inhoud], { type: soort })
    : new Blob([inhoud], { type: soort + ';charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = naam;
  a.click();
  URL.revokeObjectURL(a.href);
}

// Bestandsnaam volgens hetzelfde patroon als de server hanteert.
export function bestandsnaam(gemeente, onderwerp, extensie, datum = new Date()) {
  const d = datum.toISOString().slice(0, 10).replace(/-/g, '');
  const schoon = (t) => String(t || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${d}_${schoon(gemeente) || 'gemeente'}_${schoon(onderwerp)}.${extensie}`;
}
